import React, { useState, useEffect } from 'react';
import { X, Type, FileText, Link as LinkIcon, Palette } from 'lucide-react';

interface EditBlogTeaserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (options: {
    title: string;
    intro: string;
    imageUrl?: string;
    buttonText: string;
    buttonUrl: string;
    accentColor?: string;
    backgroundColor?: string;
  }) => void;
  initialValues?: {
    title?: string;
    intro?: string;
    imageUrl?: string;
    buttonText?: string;
    buttonUrl?: string;
    accentColor?: string;
    backgroundColor?: string;
  };
}

const ACCENT_COLORS = [
  { value: '#FF6B35', label: 'Oranje (Default)' },
  { value: '#2563eb', label: 'Blauw' },
  { value: '#16a34a', label: 'Groen' },
  { value: '#dc2626', label: 'Rood' },
  { value: '#9333ea', label: 'Paars' },
  { value: '#ea580c', label: 'Donker Oranje' },
  { value: '#0891b2', label: 'Cyaan' },
  { value: '#4b5563', label: 'Grijs' },
];

const BACKGROUND_COLORS = [
  { value: '#f9fafb', label: 'Lichtgrijs (Default)' },
  { value: '#ffffff', label: 'Wit' },
  { value: '#fef3c7', label: 'Licht Geel' },
  { value: '#dbeafe', label: 'Licht Blauw' },
  { value: '#dcfce7', label: 'Licht Groen' },
  { value: '#fee2e2', label: 'Licht Rood' },
  { value: '#fae8ff', label: 'Licht Paars' },
  { value: '#f3f4f6', label: 'Grijs' },
];

export function EditBlogTeaserModal({ isOpen, onClose, onUpdate, initialValues }: EditBlogTeaserModalProps) {
  const [title, setTitle] = useState(initialValues?.title || '');
  const [intro, setIntro] = useState(initialValues?.intro || '');
  const [imageUrl, setImageUrl] = useState(initialValues?.imageUrl || '');
  const [buttonText, setButtonText] = useState(initialValues?.buttonText || 'Lees meer');
  const [buttonUrl, setButtonUrl] = useState(initialValues?.buttonUrl || '#');
  const [accentColor, setAccentColor] = useState(initialValues?.accentColor || '#FF6B35');
  const [backgroundColor, setBackgroundColor] = useState(initialValues?.backgroundColor || '#f9fafb');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTitle(initialValues?.title || '');
      setIntro(initialValues?.intro || '');
      setImageUrl(initialValues?.imageUrl || '');
      setButtonText(initialValues?.buttonText || 'Lees meer');
      setButtonUrl(initialValues?.buttonUrl || '#');
      setAccentColor(initialValues?.accentColor || '#FF6B35');
      setBackgroundColor(initialValues?.backgroundColor || '#f9fafb');
      setError('');
    }
  }, [isOpen, initialValues]);

  if (!isOpen) return null;

  const validateUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return url.startsWith('/') || url.startsWith('#');
    }
  };

  const handleUpdate = () => {
    setError('');

    if (!title.trim()) {
      setError('Voer een titel in');
      return;
    }

    if (!intro.trim()) {
      setError('Voer een introductie tekst in');
      return;
    }

    if (!buttonText.trim()) {
      setError('Voer button tekst in');
      return;
    }

    if (!buttonUrl.trim()) {
      setError('Voer een button URL in');
      return;
    }

    if (!validateUrl(buttonUrl)) {
      setError('Voer een geldige button URL in (bijv. https://example.com)');
      return;
    }

    if (imageUrl && !validateUrl(imageUrl)) {
      setError('Voer een geldige afbeelding URL in');
      return;
    }

    onUpdate({
      title,
      intro,
      imageUrl: imageUrl || undefined,
      buttonText,
      buttonUrl,
      accentColor,
      backgroundColor
    });
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleUpdate();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center sm:justify-center justify-end z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-y-auto" onKeyDown={handleKeyDown}>
        <div className="flex items-center justify-between p-4 sm:p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
          <h2 className="text-lg sm:text-lg font-bold text-[#231F20] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#FF6B35]" />
            Blog Teaser Bewerken
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 active:text-gray-700 transition-colors p-2 -m-2 touch-manipulation"
          >
            <X className="w-6 h-6 sm:w-5 sm:h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label htmlFor="teaser-title" className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Type className="w-5 h-5 sm:w-4 sm:h-4" />
              Titel <span className="text-red-500">*</span>
            </label>
            <input
              id="teaser-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Bijv. Ontdek de Beste Tips voor..."
              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent transition-all"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="teaser-intro" className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5 sm:w-4 sm:h-4" />
              Introductie Tekst <span className="text-red-500">*</span>
            </label>
            <textarea
              id="teaser-intro"
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              placeholder="Een korte beschrijving van de blog..."
              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent transition-all min-h-[100px]"
              rows={4}
            />
          </div>

          <div>
            <label htmlFor="teaser-button-text" className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Type className="w-5 h-5 sm:w-4 sm:h-4" />
              Button Tekst <span className="text-red-500">*</span>
            </label>
            <input
              id="teaser-button-text"
              type="text"
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              placeholder="Lees meer"
              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label htmlFor="teaser-button-url" className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <LinkIcon className="w-5 h-5 sm:w-4 sm:h-4" />
              Button URL <span className="text-red-500">*</span>
            </label>
            <input
              id="teaser-button-url"
              type="url"
              value={buttonUrl}
              onChange={(e) => setButtonUrl(e.target.value)}
              placeholder="https://voorbeeld.nl/artikel"
              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Palette className="w-5 h-5 sm:w-4 sm:h-4" />
              Button Kleur
            </label>
            <div className="grid grid-cols-4 gap-2">
              {ACCENT_COLORS.map((color) => (
                <button
                  key={color.value}
                  onClick={() => setAccentColor(color.value)}
                  className={`h-10 sm:h-8 rounded-lg border-2 transition-all flex items-center justify-center touch-manipulation ${
                    accentColor === color.value
                      ? 'border-[#FF6B35] ring-4 sm:ring-2 ring-[#FF6B35] ring-opacity-50'
                      : 'border-gray-200 hover:border-gray-300 active:border-gray-400'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                >
                  {accentColor === color.value && (
                    <span className="text-white font-bold text-lg">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Palette className="w-5 h-5 sm:w-4 sm:h-4" />
              Achtergrondkleur
            </label>
            <div className="grid grid-cols-4 gap-2">
              {BACKGROUND_COLORS.map((color) => (
                <button
                  key={color.value}
                  onClick={() => setBackgroundColor(color.value)}
                  className={`h-10 sm:h-8 rounded-lg border-2 transition-all flex items-center justify-center touch-manipulation ${
                    backgroundColor === color.value
                      ? 'border-[#FF6B35] ring-4 sm:ring-2 ring-[#FF6B35] ring-opacity-50'
                      : 'border-gray-200 hover:border-gray-300 active:border-gray-400'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                >
                  {backgroundColor === color.value && (
                    <span className="text-gray-700 font-bold text-lg">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm sm:text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 sm:p-4 border-t border-gray-200 bg-gray-50 sticky bottom-0">
          <button onClick={onClose} className="btn-secondary min-h-[48px] sm:min-h-[40px] px-6 text-base sm:text-sm touch-manipulation">
            Annuleren
          </button>
          <button onClick={handleUpdate} className="btn-primary min-h-[48px] sm:min-h-[40px] px-6 text-base sm:text-sm touch-manipulation">
            Bijwerken
          </button>
        </div>
      </div>
    </div>
  );
}
